import { getPack, packIdForItem, type ItemResult, type PackId } from './packs/registry';
import { routeFraming, type ProviderRoute } from './packs/cleaning';

const forbidden = /(불법|승소|배상\s*확정|사기\s*업체|법적으로\s*유효|내용증명)/i;

/**
 * 경로가 확정되지 않았거나 중개 플랫폼이면 법정 의무로 단정하지 않는다.
 * 가사근로자법은 제7조 인증기관에만 적용되므로 중개 계약에 '법에서 정한'이라 쓰면 사실이 아니다.
 */
function anchorLine(packId: PackId, route: ProviderRoute | null): string {
  const pack = getPack(packId);
  if (pack.providerBranch) {
    if (route === 'certified_agency') {
      return `${pack.anchor.doc} ${pack.anchor.article}이 이용계약서에 적도록 정한 항목을 기준으로 정리했어요.`;
    }
    return `${pack.anchor.doc} ${pack.anchor.article}의 기재 항목을 참고 기준으로 삼아 정리했어요. 계약 형태에 따라 적용 여부가 다를 수 있어요.`;
  }
  return `${pack.anchor.doc} ${pack.anchor.article}의 항목을 참고 기준으로 삼아 정리했어요.`;
}

export type RequestOptions = { packId?: PackId; providerRoute?: ProviderRoute | null; itemIds?:string[]; tone?:'polite'|'short'; inventory?:{label:string;count:number;note:string}[]; estimateId?:string };

export function questionForItem(result:ItemResult,packId:PackId):string{
  const item=getPack(packId).checklist.find(row=>row.id===result.itemId);
  if(packId==='moving'&&result.itemId==='move-item-03'&&result.amountKrw===null&&/별도/.test(result.sourceText??''))return '견적에 사다리차가 별도로 적혀 있어요. 출발지·도착지 각각의 별도 금액과 사용 조건을 알려 주세요.';
  return item?.question??'이 항목의 금액과 조건을 확인 부탁드려요.';
}

export function buildRequest(items: ItemResult[], options: RequestOptions = {}): string {
  const packId = options.packId ?? packIdForItem(items[0]?.itemId ?? '') ?? 'moving';
  const pack = getPack(packId);
  const route = options.providerRoute ?? null;
  const unresolved = items.filter((item) => options.itemIds?options.itemIds.includes(item.itemId):['missing','conditional','unreadable'].includes(item.certainty));
  const rows = unresolved.map((result) => {
    const item = pack.checklist.find((candidate) => candidate.id === result.itemId);
    if (!item) return null;
    return `- ${item.label}: ${questionForItem(result,packId)}`;
  }).filter(Boolean);
  const inventory=options.inventory?.length?['',`짐 목록(${options.inventory.length}개)`,...options.inventory.map(i=>`- ${i.label} ${i.count}개${i.note?` · ${i.note}`:''}`)]:[];
  if(options.tone==='short'){const output=[...rows,...inventory].join('\n');if(forbidden.test(output))throw new Error('HD-LEGAL-9001');return output;}
  const output = [
    `${pack.name} ${pack.documentName.includes('계약서') ? '계약' : '견적'} 사실 확인 요청`,
    '',
    '계약 전에 서로 같은 내용으로 이해했는지 확인하려고 해요. 아래 항목이 현재 금액에 포함되는지, 별도라면 금액과 적용 조건을 회신 부탁드려요.',
    ...(pack.providerBranch && route ? ['', routeFraming[route].note] : []),
    '',
    ...(rows.length ? rows : ['- 더 여쭤볼 항목은 없어요.']),
    '',
    ...inventory,
    anchorLine(packId, route),
    '회신해 주신 내용을 기준으로 계약 여부를 결정할게요. 이 문서는 가격이나 법적 책임을 판단하는 문서가 아니라 계약 내용을 확인하기 위한 요청이에요.',
  ].join('\n');
  if (forbidden.test(output)) throw new Error('HD-LEGAL-9001');
  return output;
}

export function isSafeRequest(text: string) { return !forbidden.test(text); }
